import { FormEvent, useState } from 'react'
import { format2, formatBRL, formatInt, parseBRNumber } from './calculators'
import {
  calculateTariffFromConfig,
  currentMonthValue,
  loadOperationalConfigForDate,
  monthEndIso,
  type DynamicTariffResult,
} from '../../lib/operationalConfig'
import { applySocialTariffRule, normalizeSocialEconomies } from './socialTariff'

function Status({ kind = 'info', children }: { kind?: 'info' | 'ok' | 'warn' | 'error'; children: React.ReactNode }) {
  return <div className={`tool-status ${kind}`} role="status">{children}</div>
}

function Metric({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return <article className="tool-metric"><span>{label}</span><strong>{value}</strong>{detail && <small>{detail}</small>}</article>
}

const LABELS = { residencial: 'Residencial', comercial: 'Comercial', social: 'Social' } as const

export function TarifaEconomiasTool() {
  const [referencia, setReferencia] = useState(currentMonthValue())
  const [consumo, setConsumo] = useState('')
  const [residenciais, setResidenciais] = useState('1')
  const [comerciais, setComerciais] = useState('0')
  const [sociais, setSociais] = useState('0')
  const [socialWarning, setSocialWarning] = useState('')
  const [result, setResult] = useState<DynamicTariffResult | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setError('')
    setResult(null)
    const referenceDate = monthEndIso(referencia)
    if (!referenceDate) return setError('Informe uma referência tarifária válida.')

    setLoading(true)
    try {
      const loaded = await loadOperationalConfigForDate('tarifas', referenceDate)
      const socialEconomies = normalizeSocialEconomies(Number(sociais))
      setResult(calculateTariffFromConfig(
        applySocialTariffRule(loaded.config),
        loaded.source,
        parseBRNumber(consumo),
        { residencial: Number(residenciais), comercial: Number(comerciais), social: socialEconomies },
      ))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível calcular a tarifa.')
    } finally {
      setLoading(false)
    }
  }

  function clear() {
    setReferencia(currentMonthValue()); setConsumo(''); setResidenciais('1'); setComerciais('0'); setSociais('0'); setSocialWarning(''); setResult(null); setError('')
  }

  const summaries = result ? (['residencial', 'comercial', 'social'] as const).map((category) => {
    const rows = result.details.filter((item) => item.category === category)
    if (!rows.length) return null
    return {
      category,
      economies: rows[0].economies,
      water: rows.reduce((sum, row) => sum + row.waterSubtotal, 0),
      sewer: rows.reduce((sum, row) => sum + row.sewerSubtotal, 0),
    }
  }).filter(Boolean) as Array<{ category: keyof typeof LABELS; economies: number; water: number; sewer: number }> : []

  return (
    <section className="operational-tool">
      <div className="tool-heading">
        <div>
          <p className="eyebrow">FERRAMENTA OPERACIONAL</p>
          <h2>Valor por m³</h2>
          <p className="muted">A tabela tarifária é escolhida automaticamente pela referência informada e pode ser atualizada pelo Painel do Suite.</p>
        </div>
        <span className="status-chip">Configuração por vigência</span>
      </div>

      <div className="tool-layout">
        <form className="tool-card tool-form" onSubmit={(event) => void submit(event)}>
          <label>Referência tarifária<input type="month" value={referencia} onChange={(event) => { setReferencia(event.target.value); setResult(null) }} required /></label>
          <label>Consumo total da ligação (m³)<input inputMode="decimal" value={consumo} onChange={(event) => { setConsumo(event.target.value); setResult(null) }} placeholder="Ex.: 35" /></label>
          <label>Economias residenciais<input inputMode="numeric" value={residenciais} onChange={(event) => { setResidenciais(event.target.value); setResult(null) }} /></label>
          <label>Economias comerciais<input inputMode="numeric" value={comerciais} onChange={(event) => { setComerciais(event.target.value); setResult(null) }} /></label>
          <label>Economias sociais<input type="number" min="0" max="1" step="1" inputMode="numeric" value={sociais} onChange={(event) => {
            const rawValue = event.target.value
            if (rawValue === '') {
              setSociais('')
              setSocialWarning('')
            } else {
              const next = Number(rawValue)
              if (next > 1) {
                setSociais('1')
                setSocialWarning('Tarifa Social considera sempre 1 economia.')
              } else {
                setSociais(String(normalizeSocialEconomies(next)))
                setSocialWarning('')
              }
            }
            setResult(null)
          }} /></label>
          {socialWarning && <div className="notice info span-2" style={{ margin: 0 }} role="status">{socialWarning}</div>}
          <div className="tool-actions span-2"><button type="button" className="secondary-button" onClick={clear}>Limpar</button><button className="primary-button compact" type="submit" disabled={loading}>{loading ? 'Calculando...' : 'Calcular'}</button></div>
        </form>

        <section className="tool-card tool-result">
          <h3>Resultado</h3>
          {!result && !error && <Status>Informe referência, consumo e composição de economias.</Status>}
          {error && <Status kind="error">{error}</Status>}
          {result && (
            <>
              <Status kind={result.source === 'cache' ? 'warn' : 'ok'}>{result.source === 'cache' ? 'Cálculo realizado com a última configuração salva em cache.' : 'Cálculo tarifário concluído com configuração publicada.'}</Status>
              <div className="result-copy"><strong>{result.configTitle}</strong> · vigência {new Date(`${result.effectiveFrom}T00:00:00`).toLocaleDateString('pt-BR')}{result.decreto ? ` · Decreto ${result.decreto}` : ''}</div>
              <div className="tool-metrics three"><Metric label="Consumo da ligação" value={`${format2(result.consumptionTotal)} m³`} /><Metric label="Consumo por economia" value={`${format2(result.consumptionPerEconomy)} m³`} /><Metric label="Total de economias" value={formatInt(result.totalEconomies)} /></div>
              <div className="tool-metrics three"><Metric label="Água" value={formatBRL(result.water)} /><Metric label="Esgoto" value={formatBRL(result.sewer)} /><Metric label="Tarifa fixa" value={formatBRL(result.fixed)} /></div>

              <div className="tool-table-wrap"><table className="tool-table"><thead><tr><th>Categoria</th><th>Economias</th><th>Faixa</th><th>Consumo/economia</th><th>Consumo ligação</th><th>Água/m³</th><th>Esgoto/m³</th><th>Água faixa</th><th>Esgoto faixa</th></tr></thead><tbody>{result.details.map((item) => <tr key={`${item.category}-${item.band}`}><td>{item.categoryLabel}</td><td>{item.economies}</td><td>{item.to == null ? `acima de ${format2(item.from)}` : `${format2(item.from)} a ${format2(item.to)}`}</td><td>{format2(item.usedPerEconomy)} m³</td><td>{format2(item.usedConnection)} m³</td><td>{formatBRL(item.waterRate)}</td><td>{formatBRL(item.sewerRate)}</td><td>{formatBRL(item.waterSubtotal)}</td><td>{formatBRL(item.sewerSubtotal)}</td></tr>)}</tbody></table></div>

              <div className="tool-table-wrap"><table className="tool-table"><thead><tr><th>Categoria</th><th>Economias</th><th>Água</th><th>Esgoto</th></tr></thead><tbody>{summaries.map((item) => <tr key={item.category}><td>{LABELS[item.category]}</td><td>{item.economies}</td><td>{formatBRL(item.water)}</td><td>{formatBRL(item.sewer)}</td></tr>)}</tbody></table></div>
              <Metric label="Total a pagar" value={formatBRL(result.total)} />
            </>
          )}
        </section>
      </div>
    </section>
  )
}
